import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  BarChart,
  Bar,
  ReferenceLine,
} from "recharts";
import { api } from "@/api/client";
import type { PredictionRead, DistrictPredictionDetail, PredictionListResponse } from "@/api/types";
// districts mock is kept only for geo info (lat/lng, population, density) — no backend equivalent
import { districts as geoDistricts } from "@/mocks/districts";
import { useTheme } from "@/contexts/ThemeContext";

// Map numeric backend district IDs (1-20) to mock "D01" string IDs for geo lookup
function toMockId(numericId: number): string {
  return "D" + String(numericId).padStart(2, "0");
}

const horizons: { value: 2 | 4 | 12; label: string }[] = [
  { value: 2, label: "common_2weeks" },
  { value: 4, label: "common_1month" },
  { value: 12, label: "common_3months" },
];

interface CombinedChartData {
  date: string;
  historical: number | null;
  predicted: number | null;
  confidenceLower: number | null;
  confidenceUpper: number | null;
}

export default function Predictions() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [districtList, setDistrictList] = useState<{ id: number; name: string }[]>([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(null);
  const [detailData, setDetailData] = useState<DistrictPredictionDetail | null>(null);
  const [horizon, setHorizon] = useState<2 | 4 | 12>(2);
  const [activeTab, setActiveTab] = useState<"overview" | "features">("overview");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Load district list from predictions endpoint on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingList(true);
      try {
        const data = await api.predictions({ per_page: 200 }) as PredictionListResponse;
        if (!cancelled) {
          // Unique districts, preserving first occurrence order
          const seen = new Set<number>();
          const unique: { id: number; name: string }[] = [];
          for (const p of data?.items ?? []) {
            if (!seen.has(p.district_id)) {
              seen.add(p.district_id);
              unique.push({ id: p.district_id, name: p.district_name ?? `District ${p.district_id}` });
            }
          }
          unique.sort((a, b) => a.id - b.id);
          setDistrictList(unique);
          if (unique.length > 0) setSelectedDistrictId(unique[0].id);
        }
      } catch {
        if (!cancelled) setDistrictList([]);
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Load district detail whenever the selected district changes
  useEffect(() => {
    if (selectedDistrictId == null) return;
    let cancelled = false;
    async function load() {
      setLoadingDetail(true);
      setDetailData(null);
      try {
        const data = await api.predictionDistrict(selectedDistrictId!) as DistrictPredictionDetail;
        if (!cancelled) setDetailData(data);
      } catch {
        if (!cancelled) setDetailData(null);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedDistrictId]);

  // Find prediction for current horizon
  const prediction: PredictionRead | null = useMemo(
    () => detailData?.predictions.find((p) => p.horizon_weeks === horizon) ?? null,
    [detailData, horizon]
  );

  // Build combined chart data (historical + projected)
  const chartData = useMemo<CombinedChartData[]>(() => {
    const historical = (detailData?.historical_cases ?? [])
      .slice()
      .sort((a, b) => a.week_start.localeCompare(b.week_start))
      .slice(-12);

    const data: CombinedChartData[] = historical.map((c) => ({
      date: c.week_start.slice(5),
      historical: c.confirmed_cases,
      predicted: null,
      confidenceLower: null,
      confidenceUpper: null,
    }));

    if (prediction) {
      const lastDate = historical[historical.length - 1]?.week_start ?? "2025-05-01";
      const last = new Date(lastDate);
      const numPoints = horizon === 2 ? 2 : horizon === 4 ? 4 : 8;

      for (let i = 1; i <= numPoints; i++) {
        const futureDate = new Date(last);
        futureDate.setDate(last.getDate() + i * 7);
        const futureStr = futureDate.toISOString().split("T")[0].slice(5);

        const dampen = 1 - (i - 1) * 0.05;
        const pred = Math.round(prediction.predicted_cases * dampen);
        const lower = Math.round((prediction.confidence_lower ?? prediction.predicted_cases * 0.75) * dampen);
        const upper = Math.round((prediction.confidence_upper ?? prediction.predicted_cases * 1.35) * dampen);

        data.push({ date: futureStr, historical: null, predicted: pred, confidenceLower: lower, confidenceUpper: upper });
      }
    }

    return data;
  }, [detailData, horizon, prediction]);

  // Feature importance: convert Record<string, unknown> to sorted array
  const featureData = useMemo(() => {
    const src =
      (prediction?.feature_importance as Record<string, number> | null | undefined) ??
      (detailData?.feature_importance as Record<string, number> | null | undefined);
    if (!src) return [];
    return Object.entries(src)
      .map(([name, importance]) => ({ name, value: Math.round(Number(importance) * 100) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [prediction, detailData]);

  // Compute growth rate from latest historical vs first predicted data point
  const growthRate = useMemo(() => {
    if (!prediction) return null;
    const lastHistorical = detailData?.historical_cases.slice().sort((a, b) => a.week_start.localeCompare(b.week_start)).pop();
    if (!lastHistorical || lastHistorical.confirmed_cases === 0) return null;
    return Math.round(((prediction.predicted_cases - lastHistorical.confirmed_cases) / lastHistorical.confirmed_cases) * 100 * 10) / 10;
  }, [prediction, detailData]);

  // Derive alert level from growth rate
  const alertLevel = growthRate != null
    ? growthRate > 30 ? "High" : growthRate > 10 ? "Medium" : "Low"
    : null;

  const accuracyColor = prediction
    ? prediction.confidence >= 0.9 ? "text-risk-low"
      : prediction.confidence >= 0.8 ? "text-amber"
      : "text-risk-high"
    : "text-slate-400";

  const tooltipStyle =
    theme === "dark"
      ? { backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px", fontSize: "11px", color: "#e2e8f0" }
      : { backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "11px", color: "#1e293b" };
  const gridStroke = theme === "dark" ? "#1e293b" : "#e2e8f0";
  const tickFill = theme === "dark" ? "#64748b" : "#94a3b8";
  const yTickFill = theme === "dark" ? "#94a3b8" : "#64748b";

  // Geo info for selected district (population, density, lat/lng from mock)
  const selectedName = districtList.find((d) => d.id === selectedDistrictId)?.name ?? "";
  const geoInfo = useMemo(() => {
    if (!selectedDistrictId) return null;
    return (
      geoDistricts.find((d) => d.id === toMockId(selectedDistrictId)) ??
      geoDistricts.find((d) => d.name.toLowerCase() === selectedName.toLowerCase()) ??
      null
    );
  }, [selectedDistrictId, selectedName]);

  const horizonLabel = horizons.find((h) => h.value === horizon)?.label ?? "common_2weeks";

  if (loadingList) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-700 border-t-teal rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-xl font-display font-bold text-slate-900 dark:text-white">{t("pred_title")}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("pred_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* District selector */}
          <div className="relative">
            <i className="ri-map-pin-line absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 dark:text-slate-500" />
            <select
              value={selectedDistrictId ?? ""}
              onChange={(e) => setSelectedDistrictId(Number(e.target.value))}
              className="pl-9 pr-8 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:border-teal/50 appearance-none cursor-pointer"
            >
              {districtList.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 dark:text-slate-500 pointer-events-none" />
          </div>

          {/* Horizon selector */}
          <div className="flex items-center rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 p-1">
            {horizons.map((h) => (
              <button
                key={h.value}
                onClick={() => setHorizon(h.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  horizon === h.value
                    ? "bg-teal-muted/30 text-teal border border-teal/20"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                {t(h.label)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
        <div className="xl:col-span-3 space-y-5">
          {/* Tab switcher */}
          <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 w-fit">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === "overview"
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              <i className="ri-line-chart-line mr-1.5" />
              {t("pred_overview")}
            </button>
            <button
              onClick={() => setActiveTab("features")}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === "features"
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              <i className="ri-bar-chart-grouped-line mr-1.5" />
              {t("pred_features")}
            </button>
          </div>

          {loadingDetail ? (
            <div className="min-h-[300px] flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-slate-700 border-t-teal rounded-full animate-spin" />
            </div>
          ) : activeTab === "overview" ? (
            <>
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">
                      {selectedName} — {t(horizonLabel)}
                    </h3>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{t("pred_chart_subtitle")}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-0.5 bg-teal rounded-full" />
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">{t("pred_legend_historical")}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-0.5 bg-amber rounded-full" />
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">{t("pred_legend_predicted")}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-2 bg-amber/20 rounded" />
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">{t("pred_legend_confidence")}</span>
                    </div>
                  </div>
                </div>

                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="confidenceGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: tickFill }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: tickFill }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(value: number, name: string) => {
                          if (value === null || value === undefined) return ["", ""];
                          const labelMap: Record<string, string> = {
                            historical: t("pred_legend_historical"),
                            predicted: t("pred_legend_predicted"),
                            confidenceLower: "Lower Bound",
                            confidenceUpper: "Upper Bound",
                          };
                          return [value.toLocaleString(), labelMap[name] || name];
                        }}
                      />
                      <Area type="monotone" dataKey="confidenceUpper" stroke="none" fill="url(#confidenceGrad)" name="confidenceUpper" />
                      <Area type="monotone" dataKey="confidenceLower" stroke="none" fill={theme === "dark" ? "#0f172a" : "#ffffff"} name="confidenceLower" />
                      <Line type="monotone" dataKey="historical" stroke="#2dd4bf" strokeWidth={2.5} dot={{ r: 3, fill: "#2dd4bf" }} activeDot={{ r: 5, fill: "#2dd4bf", stroke: theme === "dark" ? "#0f172a" : "#ffffff", strokeWidth: 2 }} name="historical" connectNulls={false} />
                      <Line type="monotone" dataKey="predicted" stroke="#f59e0b" strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 3, fill: "#f59e0b" }} activeDot={{ r: 5, fill: "#f59e0b", stroke: theme === "dark" ? "#0f172a" : "#ffffff", strokeWidth: 2 }} name="predicted" connectNulls={false} />
                      <ReferenceLine x={chartData.find((d) => d.predicted !== null)?.date} stroke={theme === "dark" ? "#334155" : "#cbd5e1"} strokeDasharray="4 4" label={{ value: t("pred_prediction_start"), position: "top", fill: tickFill, fontSize: 9 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4 text-center">
                  <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("pred_predicted_cases")}</p>
                  <p className="text-xl font-display font-bold text-slate-900 dark:text-white mt-1">
                    {prediction?.predicted_cases.toLocaleString() ?? "—"}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4 text-center">
                  <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("dash_growth_rate")}</p>
                  <p className={`text-xl font-display font-bold mt-1 ${
                    growthRate != null
                      ? growthRate > 30 ? "text-risk-high" : growthRate > 10 ? "text-amber" : "text-risk-low"
                      : "text-slate-400"
                  }`}>
                    {growthRate != null ? `${growthRate > 0 ? "+" : ""}${growthRate}%` : "—"}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4 text-center">
                  <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("pred_confidence")}</p>
                  <p className={`text-xl font-display font-bold mt-1 ${accuracyColor}`}>
                    {prediction ? `${Math.round(prediction.confidence * 100)}%` : "—"}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4 text-center">
                  <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("pred_alert_level")}</p>
                  <p className={`text-xl font-display font-bold mt-1 ${
                    alertLevel === "High" ? "text-risk-high" : alertLevel === "Medium" ? "text-amber" : alertLevel === "Low" ? "text-risk-low" : "text-slate-400"
                  }`}>
                    {alertLevel ? (alertLevel === "High" ? t("common_high") : alertLevel === "Medium" ? t("common_medium") : t("common_low")) : "—"}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("pred_top_drivers")}</h3>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {t("pred_drivers_desc")} — {selectedName}
                  </p>
                </div>
                {featureData.length > 0 ? (
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={featureData} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} horizontal={false} />
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: tickFill }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: yTickFill }} axisLine={false} tickLine={false} width={140} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`${value}%`, "Importance"]} />
                        <Bar dataKey="value" fill="#2dd4bf" radius={[0, 4, 4, 0]} barSize={24} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[200px] flex items-center justify-center">
                    <p className="text-sm text-slate-500">{t("pred_select_district")}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { name: "Prev. Cases (8wk)", desc: "Cumulative confirmed cases over the past 8 weeks — strongest predictor of outbreak trajectory", impact: "Very High" },
                  { name: "Weekly Rainfall", desc: "Accumulated rainfall in mm — drives mosquito breeding site proliferation", impact: "High" },
                  { name: "Avg Temperature", desc: "Mean weekly temperature — affects parasite development speed in vectors", impact: "High" },
                  { name: "Population Density", desc: "Residents per km² — higher density accelerates transmission chains", impact: "Medium" },
                  { name: "Seasonality Index", desc: "Normalized seasonal pattern score — captures recurring epidemic waves", impact: "Medium" },
                  { name: "Lag Cases (4wk)", desc: "Confirmed cases from 4 weeks prior — early warning signal for exponential growth", impact: "Medium" },
                ].map((f) => (
                  <div key={f.name} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-200">{f.name}</h4>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${f.impact === "Very High" ? "bg-risk-high/15 text-risk-high" : f.impact === "High" ? "bg-amber/15 text-amber" : "bg-teal-muted/30 text-teal"}`}>
                        {f.impact}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right sidebar */}
        <div className="space-y-5">
          {/* District info */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display mb-1">
              {detailData?.district_name ?? selectedName}
            </h3>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-4">
              {geoInfo?.region ?? "—"} {t("pred_region_label")}
            </p>
            <div className="space-y-3">
              {[
                {
                  label: t("pred_population"),
                  value: geoInfo ? geoInfo.population.toLocaleString() : "—",
                  icon: "ri-team-line",
                },
                {
                  label: t("pred_density"),
                  value: geoInfo ? `${geoInfo.density}/km²` : "—",
                  icon: "ri-bar-chart-box-line",
                },
                {
                  label: t("pred_coordinates"),
                  value: geoInfo ? `${geoInfo.lat.toFixed(2)}, ${geoInfo.lng.toFixed(2)}` : "—",
                  icon: "ri-map-pin-line",
                },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between py-2 border-b border-slate-200/50 dark:border-slate-800/50 last:border-0">
                  <div className="flex items-center gap-2">
                    <i className={`${item.icon} text-xs text-slate-400 dark:text-slate-500`} />
                    <span className="text-xs text-slate-500 dark:text-slate-400">{item.label}</span>
                  </div>
                  <span className="text-xs font-mono text-slate-800 dark:text-slate-200">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Model info */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display mb-4">{t("pred_model_info")}</h3>
            <div className="space-y-3">
              {[
                { label: t("pred_algorithm"), value: "XGBoost Regressor", color: "text-teal" },
                { label: t("pred_model_version"), value: prediction?.model_version ?? "—", color: "text-slate-800 dark:text-slate-200" },
                { label: t("pred_input_features"), value: featureData.length > 0 ? String(featureData.length) : "8", color: "text-slate-800 dark:text-slate-200" },
                { label: t("pred_training_data"), value: "2 years weekly", color: "text-slate-800 dark:text-slate-200" },
                { label: t("pred_retrain"), value: "Weekly", color: "text-slate-800 dark:text-slate-200" },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 dark:text-slate-400">{item.label}</span>
                  <span className={`text-xs font-mono ${item.color}`}>{item.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-teal animate-pulse" />
                <span className="text-xs text-teal">{t("pred_pipeline_healthy")}</span>
              </div>
            </div>
          </div>

          {/* Confidence interval */}
          {prediction && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display mb-4">{t("pred_confidence_range")}</h3>
              <div className="relative h-4 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden mb-3">
                {prediction.confidence_lower != null && prediction.confidence_upper != null && prediction.confidence_upper > 0 && (
                  <>
                    <div
                      className="absolute top-0 bottom-0 bg-amber/30 rounded-full"
                      style={{
                        left: `${(prediction.confidence_lower / prediction.confidence_upper) * 100}%`,
                        right: "0%",
                      }}
                    />
                    <div className="absolute top-0 bottom-0 w-0.5 bg-amber" style={{ left: `${(prediction.confidence_lower / prediction.confidence_upper) * 100}%` }} />
                    <div className="absolute top-0 bottom-0 w-0.5 bg-amber" style={{ right: "0%" }} />
                  </>
                )}
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-400 dark:text-slate-400 font-mono">
                <span>{(prediction.confidence_lower ?? 0).toLocaleString()}</span>
                <span className="text-amber">{prediction.predicted_cases.toLocaleString()}</span>
                <span>{(prediction.confidence_upper ?? 0).toLocaleString()}</span>
              </div>
              <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-2">{t("pred_ci_desc")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
